import type { ReactNode } from 'react'
import { Link, getRouteApi } from '@tanstack/react-router'
import { MailIcon } from 'lucide-react'
import type { OrderState, OrderView, SessionRequest } from '@hedgehog/shared'
import { api, ApiRequestError } from '@/lib/api'
import { ErrorState, errorStatus } from '@/components/app/states'
import { buttonVariants } from '@/components/ui/button'

/**
 * Exchanges the emailed access token for a session cookie. A stale token is not fatal: an
 * existing cookie may still be valid, and the order call that follows decides.
 */
export async function startSession(orderId: string, token: string, signal?: AbortSignal): Promise<void> {
  try {
    const body: SessionRequest = { orderId, token }
    await api<unknown>('/session', { method: 'POST', json: body, signal })
  } catch (err) {
    if (!(err instanceof ApiRequestError) || err.status >= 500) throw err
  }
}

/** Loader data of the /o/$orderId layout, readable from any child route. */
export const orderApi = getRouteApi('/o/$orderId')

export function useOrder(): OrderView {
  return orderApi.useLoaderData()
}

type TabPath = '/o/$orderId' | '/o/$orderId/approve' | '/o/$orderId/domain' | '/o/$orderId/messages' | '/o/$orderId/editor' | '/o/$orderId/articles' | '/o/$orderId/billing'

const APPROVAL = new Set<OrderState>(['awaiting_approval', 'changes_requested'])
const DOMAIN = new Set<OrderState>(['awaiting_dns', 'live', 'handed_over'])
const SITE = new Set<OrderState>(['live', 'handed_over'])
const CLOSED = new Set<OrderState>(['declined', 'expired', 'refunded'])
const BILLING = new Set<OrderState>([
  'paid',
  'migrating',
  'in_review',
  'needs_human',
  'awaiting_approval',
  'changes_requested',
  'awaiting_dns',
  'live',
  'handed_over',
  'refunded',
])

export interface OrderTab {
  to: TabPath
  label: string
  available: (o: OrderView) => boolean
  /** Shown when someone opens the tab before it applies. */
  opensWhen: string
}

export const ORDER_TABS: OrderTab[] = [
  { to: '/o/$orderId', label: 'Status', available: () => true, opensWhen: '' },
  {
    to: '/o/$orderId/approve',
    label: 'Approve',
    available: (o) => APPROVAL.has(o.state),
    opensWhen: 'This opens when your full site is ready for you to check.',
  },
  {
    to: '/o/$orderId/domain',
    label: 'Domain',
    available: (o) => DOMAIN.has(o.state),
    opensWhen: 'This opens after you approve your site. It shows how to connect your domain.',
  },
  {
    to: '/o/$orderId/messages',
    label: 'Messages',
    available: (o) => SITE.has(o.state),
    opensWhen: 'Messages from your contact form show here once your site is live.',
  },
  {
    to: '/o/$orderId/editor',
    label: 'Edit your site',
    available: (o) => SITE.has(o.state),
    opensWhen: 'You can edit your site once it is live.',
  },
  {
    to: '/o/$orderId/articles',
    label: 'Articles',
    // Sites with the blog feature only (sites built before features existed have it).
    available: (o) => SITE.has(o.state) && !!o.site?.features.includes('blog'),
    opensWhen: 'Articles open once your site is live, on sites with a blog.',
  },
  {
    to: '/o/$orderId/billing',
    label: 'Billing',
    available: (o) => BILLING.has(o.state),
    opensWhen: 'Billing details show here after payment.',
  },
]

/** Renders children when the tab applies to the order's state, otherwise a short explanation. */
export function TabGate({ tab, children }: { tab: TabPath; children: ReactNode }) {
  const order = useOrder()
  const def = ORDER_TABS.find((t) => t.to === tab)
  if (!def || def.available(order)) return <>{children}</>
  return (
    <div className="flex flex-col items-start gap-3 py-8">
      <p className="text-muted-foreground">
        {CLOSED.has(order.state)
          ? 'This order is closed, so this page is not available. Reply to any of our emails if you think this is a mistake.'
          : def.opensWhen}
      </p>
      <Link to="/o/$orderId" params={{ orderId: order.id }} className={buttonVariants({ variant: 'outline' })}>
        Back to status
      </Link>
    </div>
  )
}

/** Shown on 401/403 from any order call: the session cookie is missing or expired. */
export function SignedOut() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-16 text-center">
      <MailIcon className="size-8 text-muted-foreground" aria-hidden />
      <h1 className="text-2xl font-semibold">Open the link from your latest email</h1>
      <p className="text-muted-foreground">
        For your security, this page opens from the personal link in our emails. Find our most recent email and click its
        button; the link signs you in on this device.
      </p>
    </div>
  )
}

/** errorComponent body for order routes: sign-in prompt on 401/403, not-found on 404, retry otherwise. */
export function OrderError({ error, title }: { error: unknown; title: string }) {
  const status = errorStatus(error)
  if (status === 401 || status === 403) return <SignedOut />
  if (status === 404)
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-16 text-center">
        <h1 className="text-2xl font-semibold">We could not find this order</h1>
        <p className="text-muted-foreground">Check that you opened the full link from our email.</p>
      </div>
    )
  return <ErrorState error={error} title={title} />
}

/** Customer wording for each state, used by the status timeline. */
export const CUSTOMER_STEP_LABELS: Record<OrderState, string> = {
  audited: 'Site audited',
  declined: 'Not something we can rebuild yet',
  preview_requested: 'Preview requested',
  preview_ready: 'Preview ready',
  expired: 'Preview expired',
  paid: 'Payment received',
  migrating: 'Rebuilding your full site',
  in_review: 'Quality check',
  needs_human: 'Our team is taking a closer look',
  awaiting_approval: 'Ready for your approval',
  changes_requested: 'Applying your changes',
  awaiting_dns: 'Connecting your domain',
  live: 'Your new site is live',
  handed_over: 'Editor access set up',
  refunded: 'Refunded',
}

/** The normal path an order takes, in order. Detours (changes, needs_human) and final states are not listed. */
export const HAPPY_PATH: OrderState[] = [
  'preview_requested',
  'preview_ready',
  'paid',
  'migrating',
  'in_review',
  'awaiting_approval',
  'awaiting_dns',
  'live',
  'handed_over',
]

/** The one action the customer should take next, if any. */
export function primaryAction(order: OrderView): { label: string; to: TabPath } | { label: string; href: string } | null {
  switch (order.state) {
    case 'preview_ready':
      return order.previewToken ? { label: 'See your preview', href: `/preview/${order.previewToken}` } : null
    case 'awaiting_approval':
      return { label: 'Check and approve your site', to: '/o/$orderId/approve' }
    case 'awaiting_dns':
      return { label: 'Connect your domain', to: '/o/$orderId/domain' }
    case 'live':
    case 'handed_over':
      return order.site?.siteUrl ? { label: 'Visit your site', href: order.site.siteUrl } : null
    default:
      return null
  }
}
